import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, Link } from 'react-router-dom';
import { ProductCard } from '@/components/loja/ProductCard';
import { Search, Package, Lock, Crown, Sparkles, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import api from '@/lib/api';
import { useToast } from '@/hooks/useToast';

export function SalaSecreta() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Detecção avançada: Pode vir como ?ticket=... ou apenas ?TOKEN_ALEATORIO
  const getInitialTicket = () => {
    // 1. Checa se há uma string "pura" após o ? (ex: ?4A8C...)
    const rawSearch = window.location.search; // ex: "?ABC123XYZ"
    if (rawSearch && rawSearch.length > 5) {
       // Pega tudo após o ? e limpa eventuais outros params
       const possibleToken = rawSearch.substring(1).split('&')[0];
       // Somente se for um token limpo (sem =) tratamos como o ticket direto
       if (!possibleToken.includes('=') && possibleToken.length > 10) {
          return possibleToken;
       }
    }

    // 2. Fallback para ?ticket=... ou ?s=...
    const ticketParam = searchParams.get('ticket') || searchParams.get('s');
    if (ticketParam) return ticketParam;

    return null;
  };

  const ticketFromUrl = getInitialTicket();
  
  const [senhaInput, setSenhaInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [acessoLiberado, setAcessoLiberado] = useState(false);
  
  const [produtos, setProdutos] = useState<any[]>([]);
  const [cupom, setCupom] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  const [busca, setBusca] = useState('');
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string | null>(null);

  const validateAccess = useCallback(async (ticket: string) => {
    if (!ticket) return;
    
    console.log('[Sala Secreta] Tentando validar ticket:', ticket);
    setLoading(true);
    setErrorMsg(null);

    try {
      const response = await api.post('/unboxing/acessar-sala', { ticket });
      
      if (response.data.success) {
        console.log('[Sala Secreta] Acesso concedido!');
        const { cupom: cupomData, produtos: prds } = response.data;
        
        // Aplica o desconto nos preços para exibição
        const discountValue = Number(cupomData.desconto);
        const discountedProducts = prds.map((p: any) => {
          const originalPrice = Number(p.precoVenda);
          const newPrice = originalPrice - (originalPrice * (discountValue / 100));
          return {
            ...p,
            precoVenda: newPrice,
            precoOriginal: originalPrice
          };
        });

        setProdutos(discountedProducts);
        setCupom(cupomData);
        setAcessoLiberado(true);
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Senha inválida ou o limite de uso desse QR Code expirou.';
      console.error('[Sala Secreta] Erro de acesso:', msg);
      
      setErrorMsg(msg);
      // Se veio da URL e falhou, limpa a URL para não ficar tentando em loop
      if (ticket === ticketFromUrl) {
        setSearchParams({}, { replace: true });
      }
      
      toast({
        title: 'Acesso Negado',
        description: msg,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [ticketFromUrl, setSearchParams, toast]);

  // Efeito de entrada: Se houver ticket na URL, tenta desbloquear na hora
  useEffect(() => {
    if (ticketFromUrl && !acessoLiberado && !loading && !errorMsg) {
      validateAccess(ticketFromUrl);
    }
  }, [ticketFromUrl, acessoLiberado, loading, errorMsg, validateAccess]);

  const handleLoginManual = (e: React.FormEvent) => {
    e.preventDefault();
    validateAccess(senhaInput);
  };

  // Extrair categorias exclusivas dos produtos retornados
  const categoriasOptions = Array.from(new Set(produtos.map(p => p.categoriaId)))
    .map(id => produtos.find(p => p.categoriaId === id)?.categoria)
    .filter(Boolean);

  // Filtragem local dos produtos autorizados
  const produtosFiltrados = produtos.filter((p) => {
    const matchesBusca = p.nome.toLowerCase().includes(busca.toLowerCase());
    const matchesCategoria = categoriaSelecionada ? p.categoriaId === categoriaSelecionada : true;
    return matchesBusca && matchesCategoria;
  });

  // ESTADO 1: TELA DE VERIFICAÇÃO AUTOMÁTICA (QR CODE DETECTADO)
  if (!acessoLiberado && ticketFromUrl && loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center"
        >
          <div className="relative mb-8">
            <div className="w-24 h-24 bg-gray-900 rounded-[2rem] flex items-center justify-center border border-gray-800 shadow-[0_0_50px_rgba(255,107,53,0.2)]">
               <Sparkles className="w-10 h-10 text-fort3d-laranja animate-pulse" />
            </div>
            <div className="absolute -inset-4 border border-fort3d-laranja/20 rounded-[2.5rem] animate-ping [animation-duration:3s]" />
          </div>
          
          <h2 className="text-3xl font-black text-white italic uppercase tracking-tighter mb-3">
            Revelando <span className="text-fort3d-laranja">Sala Secreta</span>
          </h2>
          <p className="text-gray-500 font-medium max-w-xs leading-relaxed">
            Sua chave de Unboxing está sendo validada nos nossos servidores...
          </p>
          
          <div className="mt-10 flex gap-3">
            {[0, 1, 2].map((i) => (
              <div 
                key={i} 
                className="w-3 h-3 bg-fort3d-laranja rounded-full animate-bounce" 
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  // ESTADO 2: TELA DE LOGIN MANUAL (PEDIR SENHA)
  if (!acessoLiberado) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full bg-gray-900 border border-gray-800 p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden"
        >
          {loading && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md z-20 flex items-center justify-center">
              <div className="animate-spin w-12 h-12 border-4 border-fort3d-laranja border-t-transparent rounded-full" />
            </div>
          )}
          
          <div className="w-20 h-20 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-8 border border-gray-700 shadow-inner">
            <Lock className="w-10 h-10 text-fort3d-laranja" />
          </div>
          
          <h1 className="text-4xl font-black text-white mb-3 tracking-tighter uppercase italic">
            SALA <span className="text-fort3d-laranja">SECRETA</span>
          </h1>
          <p className="text-gray-400 mb-10 leading-relaxed text-sm">
            Este é um ambiente restrito. Insira a <span className="text-white font-bold">Chave de Unboxing</span> impressa na sua tag para visualizar os produtos e descontos VIP.
          </p>
          
          <form onSubmit={handleLoginManual} className="space-y-5">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-fort3d-laranja/20 rounded-2xl blur opacity-0 group-focus-within:opacity-100 transition duration-500" />
              <Input
                type="text"
                placeholder="DIGITE SUA CHAVE..."
                className="relative h-16 bg-black/80 border-gray-700 text-center uppercase text-xl font-black tracking-[0.2em] placeholder:text-gray-700 focus:border-fort3d-laranja rounded-2xl"
                value={senhaInput}
                onChange={(e) => setSenhaInput(e.target.value.toUpperCase())}
                required
              />
            </div>
            
            <Button 
              type="submit"
              className="w-full bg-fort3d-laranja hover:bg-fort3d-laranja/90 text-white font-black h-14 uppercase tracking-widest text-sm rounded-2xl shadow-lg shadow-fort3d-laranja/20"
              isLoading={loading}
            >
              DESTRAVAR ACESSO
            </Button>
          </form>
          
          <div className="mt-10 pt-8 border-t border-gray-800/50">
             <Link to="/catalogo">
               <Button variant="ghost" className="w-full text-gray-500 hover:text-white hover:bg-white/5 font-bold uppercase tracking-tighter text-xs">
                 <ArrowLeft className="w-4 h-4 mr-2" /> Voltar ao Catálogo Público
               </Button>
             </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  // ESTADO 3: SALA LIBERADA (VITRINE)
  return (
    <div className="min-h-screen bg-black py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Exclusivo de Alto Padrão */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 relative overflow-hidden p-10 rounded-[3rem] bg-gradient-to-br from-gray-900 via-gray-900 to-black border border-fort3d-laranja/20 shadow-2xl"
        >
          <div className="absolute top-0 right-0 p-6 opacity-5 select-none pointer-events-none">
            <Crown className="w-64 h-64 text-fort3d-laranja" />
          </div>
          
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2.5 px-4 py-1.5 bg-fort3d-laranja/10 text-fort3d-laranja rounded-full text-xs font-black uppercase tracking-[0.3em] border border-fort3d-laranja/20">
                <Sparkles className="w-4 h-4" /> ACESSO VIP ATIVO
              </div>
              
              <h1 className="text-5xl sm:text-7xl font-black text-white italic tracking-tighter uppercase leading-[0.9]">
                SALA <br/><span className="text-fort3d-laranja">SECRETA</span>
              </h1>
              
              <p className="text-gray-400 max-w-xl text-lg leading-relaxed font-medium">
                Seu ticket concedeu <span className="text-white font-bold inline-flex bg-white/10 px-2 py-0.5 rounded">{cupom?.desconto}% DE DESCONTO</span> em todo o catálogo abaixo. <br className="hidden sm:block" />
                Aproveite enquanto sua chave é válida!
              </p>
            </div>
            
            <div className="flex flex-col gap-4">
               <div className="bg-black/40 backdrop-blur-sm border border-gray-800 p-6 rounded-3xl">
                  <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mb-1">Chave Ativa</p>
                  <code className="text-fort3d-laranja font-mono font-bold text-sm">{cupom?.codigo || '---'}</code>
               </div>
               <Link to="/catalogo">
                 <Button variant="outline" className="w-full border-gray-800 text-gray-400 hover:bg-gray-800 hover:text-white rounded-2xl h-12">
                   <ArrowLeft className="w-4 h-4 mr-2" /> SAIR DA SALA
                 </Button>
               </Link>
            </div>
          </div>
        </motion.div>

        {/* Busca e Filtros Premium */}
        <div className="mb-16 space-y-8">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="relative flex-1 group">
              <div className="absolute -inset-1 bg-gradient-to-r from-fort3d-laranja/20 to-transparent rounded-2xl blur opacity-25 group-focus-within:opacity-50 transition duration-500" />
              <Search className="absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-600 group-focus-within:text-fort3d-laranja transition-colors" />
              <input
                placeholder="Buscar modelos exclusivos..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="relative w-full h-16 bg-gray-900/40 border border-gray-800 rounded-2xl pl-14 text-white placeholder:text-gray-700 focus:outline-none focus:border-fort3d-laranja transition-all shadow-inner text-lg"
              />
            </div>
          </div>

          {/* Category tabs */}
          {categoriasOptions.length > 0 && (
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setCategoriaSelecionada(null)}
                className={`px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 ${
                  !categoriaSelecionada
                    ? 'bg-fort3d-laranja text-white shadow-[0_10px_20px_rgba(237,115,71,0.3)]'
                    : 'bg-gray-900/50 text-gray-600 hover:bg-gray-800 hover:text-gray-400 border border-gray-800'
                }`}
              >
                Tudo
              </button>
              {categoriasOptions.map((categoria: any) => (
                <button
                  key={categoria.id}
                  onClick={() => setCategoriaSelecionada(categoria.id)}
                  className={`px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 ${
                    categoriaSelecionada === categoria.id
                      ? 'bg-fort3d-laranja text-white shadow-[0_10px_20px_rgba(237,115,71,0.3)]'
                      : 'bg-gray-900/50 text-gray-600 hover:bg-gray-800 hover:text-gray-400 border border-gray-800'
                  }`}
                >
                  {categoria.nome}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Grid de Produtos com Efeito de Revelação */}
        {produtosFiltrados.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-10">
            {produtosFiltrados.map((produto: any, index: number) => (
              <ProductCard 
                key={produto.id} 
                produto={produto} 
                index={index} 
              />
            ))}
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-40 bg-gray-900/10 rounded-[3rem] border-2 border-dashed border-gray-800"
          >
            <Package className="mx-auto h-20 w-20 text-gray-800 mb-6" />
            <h3 className="text-3xl font-black text-gray-600 mb-4 uppercase italic italic tracking-tighter">
              Ainda Sem Tesouros
            </h3>
            <p className="text-gray-700 max-w-sm mx-auto font-medium">
              Não encontramos produtos para esta categoria na Sala Secreta. Explore as outras abas!
            </p>
          </motion.div>
        )}
      </div>
      
      {/* Footer minimalista VIP */}
      <div className="mt-40 border-t border-gray-900/50 pt-12 pb-20 text-center">
         <div className="inline-block px-10 py-3 border border-gray-900 rounded-full">
            <p className="text-[10px] text-gray-800 font-extrabold uppercase tracking-[0.8em]">
              FORT3D &bull; CIRCULO INTERNO &bull; VIP 
            </p>
         </div>
      </div>
    </div>
  );
}

export default SalaSecreta;
